import React, { useEffect, useState, useMemo } from "react";
import SummaryApi from "../common";
import { toast } from "react-toastify";

import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { FaSyncAlt } from "react-icons/fa";
import {
  PhoneOutlined,
  HeartOutlined,
  SmileOutlined,
  CloseCircleOutlined,
  QuestionCircleOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { Card, Spin, Row, Col, DatePicker, Avatar } from "antd";
const { RangePicker } = DatePicker;

/* ---------- date filter helpers (same as PerformanceOverview) ---------- */
const FILTERS = [
  { key: "today", label: "Today", days: 0 },
  { key: "7days", label: "7 days", days: 7 },
  { key: "30days", label: "30 days", days: 30 },
  { key: "all", label: "All", days: null },
];
const API_FMT = "YYYY-MM-DD";
const UI_FMT = "DD-MM-YYYY";

const RANGE_PRESETS = [
  { label: "Last 7 Days", value: () => [dayjs().subtract(6, "day"), dayjs()] },
  {
    label: "Last 14 Days",
    value: () => [dayjs().subtract(13, "day"), dayjs()],
  },
  {
    label: "Last 30 Days",
    value: () => [dayjs().subtract(29, "day"), dayjs()],
  },
  { label: "This Month", value: () => [dayjs().startOf("month"), dayjs()] },
];

const getTodayRange = () => [dayjs(), dayjs()];

const getDateRange = (key, customRange) => {
  if (key === "all") {
    return {
      start_date: customRange[0].format(API_FMT),
      end_date: customRange[1].format(API_FMT),
    };
  }
  const f = FILTERS.find((x) => x.key === key);
  const end = dayjs();
  return {
    start_date: end.subtract(f.days, "day").format(API_FMT),
    end_date: end.format(API_FMT),
  };
};

const disableFuture = (current) => current && current.isAfter(dayjs(), "day");
const getEmployeeImageUrl = (path) => {
  if (!path) return null;
  const base = String(SummaryApi.employeeImage.url).replace(/\/+$/, "");
  const clean = String(path).replace(/^\/+/, "");
  return `${base}/${clean}`;
};
const TeamLeadEmployees = () => {
  const [loading, setLoading] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [teamLeadName, setTeamLeadName] = useState("");
  const [memberType, setMemberType] = useState(null);
  const navigate = useNavigate();

  // filter state
  const [filterKey, setFilterKey] = useState("today");
  const [customRange, setCustomRange] = useState(getTodayRange);

  const { start_date: startDate, end_date: endDate } = useMemo(
    () => getDateRange(filterKey, customRange),
    [filterKey, customRange],
  );

  // memberType (runs once)
  useEffect(() => {
    const encodedMemberType = sessionStorage.getItem("K3M8X1JQ5VZ4B7PWTL9");
    setMemberType(encodedMemberType ? atob(encodedMemberType) : null);
  }, []);

  // fetch employees (re-runs whenever the date range changes)
  useEffect(() => {
    let cancelled = false;

    const fetchTeamLeadEmployees = async () => {
      try {
        setLoading(true);
        const encodedEmployeeId = sessionStorage.getItem(
          "H7Z4L9VD2XK5JW8QRYT6",
        );
        if (!encodedEmployeeId) {
          toast.error("Employee ID not found. Please log in again.");
          return;
        }
        const teamLeadEmpId = atob(encodedEmployeeId);

        const response = await fetch(SummaryApi.TeamLeadEmp.url, {
          method: SummaryApi.TeamLeadEmp.method,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer sandeep_university_916",
          },
          body: JSON.stringify({
            team_lead_emp_id: teamLeadEmpId,
            start_date: startDate,
            end_date: endDate,
          }),
        });

        if (!response.ok) throw new Error("Failed to fetch employee data");
        const data = await response.json();
        if (cancelled) return;

        if (data?.team_lead_full_name)
          setTeamLeadName(data.team_lead_full_name);

        if (Array.isArray(data?.data)) {
          setEmployees(data.data);
        } else {
          toast.info("No employees assigned to this Team Lead.");
          setEmployees([]);
        }
      } catch (error) {
        if (!cancelled) toast.error(`Error: ${error.message}`);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchTeamLeadEmployees();
    return () => {
      cancelled = true;
    };
  }, [startDate, endDate]);

  const isTeamLead = useMemo(() => memberType === "Team Lead", [memberType]);

  const handleReset = () => {
    setFilterKey("today");
    setCustomRange(getTodayRange());
  };

  const handleRangeChange = (values) => {
    setCustomRange(
      values && values[0] && values[1]
        ? [values[0], values[1]]
        : getTodayRange(),
    );
  };

  return (
    <div style={{ padding: "20px" }}>
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3 mb-6">
        <h2 className="text-2xl font-bold">
          Team Lead:{" "}
          <span style={{ color: "#3e0f94" }}>{teamLeadName || "..."}</span>
        </h2>

        <div className="flex flex-col gap-3 lg:items-end">
          <div className="flex flex-wrap items-center gap-3">
            {/* Filter pills */}
            <div className="inline-flex bg-white rounded-full border border-slate-200 p-1 shadow-sm">
              {FILTERS.map((f) => (
                <button
                  key={f.key}
                  onClick={() => setFilterKey(f.key)}
                  className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${filterKey === f.key
                    ? "bg-blue-200 text-slate-800 shadow-inner"
                    : "text-slate-600 hover:bg-slate-50"
                    }`}
                >
                  {f.label}
                </button>
              ))}
            </div>

            {/* Reset icon (only on "All") */}
            {filterKey === "all" && (
              <button
                type="button"
                onClick={handleReset}
                title="Reset to default"
                aria-label="Reset to default"
                className="w-9 h-9 -ml-1 inline-flex items-center justify-center rounded-full bg-white border border-slate-200
                           text-slate-500 shadow-sm hover:text-blue-600 hover:border-blue-300 hover:rotate-180 transition-all duration-300"
              >
                <FaSyncAlt size={13} />
              </button>
            )}

            {!isTeamLead && (
              <button
                onClick={() => navigate(-1)}
                className="bg-blue-600 text-white px-4 py-2 rounded border"
              >
                Back
              </button>
            )}
          </div>

          {/* Date range picker (only on "All") */}
          {filterKey === "all" && (
            <RangePicker
              value={customRange}
              onChange={handleRangeChange}
              presets={RANGE_PRESETS}
              format={UI_FMT}
              allowClear={false}
              disabledDate={disableFuture}
              placeholder={["Start date", "End date"]}
            />
          )}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-48">
          <Spin size="large" />
        </div>
      ) : employees.length === 0 ? (
        <p style={{ fontSize: "16px", color: "#555" }}>
          No employees assigned.
        </p>
      ) : (
        <Row gutter={[16, 16]}>
          {employees.map((emp) => (
            <Col xs={24} sm={12} md={8} lg={6} key={emp.assign_emp_id}>
              <Card
                hoverable
                onClick={() => navigate(`/my-employees/${emp.assign_emp_id}`)}
                style={{
                  borderRadius: "10px",
                  border: "1px solid #e0e0e0",
                  boxShadow: "0 4px 8px rgba(0,0,0,0.05)",
                  transition: "transform 0.2s",
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.transform = "scale(1.03)")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.transform = "scale(1)")
                }
                title={
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <Avatar
                        size={44}
                        src={getEmployeeImageUrl(emp.employee_image)}
                        icon={<UserOutlined />}
                        style={{
                          backgroundColor: "#e9ddff",
                          color: "#3e0f94",
                          flexShrink: 0,
                        }}
                      />
                      <span
                        style={{ color: "#3e0f94", fontWeight: "bold" }}
                        className="truncate"
                      >
                        {emp.assign_full_name}
                      </span>
                    </div>
                    <span
                      style={{
                        color: "#3e0f94",
                        fontWeight: "bold",
                        fontSize: 13,
                      }}
                    >
                      {emp.mobile_number || "N/A"}
                    </span>
                  </div>
                }
              >
                <p>
                  <HeartOutlined style={{ color: "#eb2f96", marginRight: 6 }} />
                  <strong>Most Interested:</strong> {emp.most_interested_count}
                </p>
                <p>
                  <SmileOutlined style={{ color: "#faad14", marginRight: 6 }} />
                  <strong>Interested:</strong> {emp.interested_count}
                </p>
                <p>
                  <CloseCircleOutlined
                    style={{ color: "#ff4d4f", marginRight: 6 }}
                  />
                  <strong>Not Interested:</strong> {emp.not_interested_count}
                </p>
                <p>
                  <QuestionCircleOutlined
                    style={{ color: "#595959", marginRight: 6 }}
                  />
                  <strong>Not Answered:</strong> {emp.not_answered_count}
                </p>
                <p>
                  <PhoneOutlined style={{ color: "#13c2c2", marginRight: 6 }} />
                  <strong>Call Back:</strong> {emp.call_back_count}
                </p>
              </Card>
            </Col>
          ))}
        </Row>
      )}
    </div>
  );
};

export default TeamLeadEmployees;